import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Sparkles, FileText, Download, ExternalLink, CheckCircle2 } from "lucide-react";
import { api, errMsg, fmtDate, media, streamText } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const sel = "h-10 w-full rounded-md border border-white/10 bg-[#07090E] px-3 text-sm";
const SERVICES = [["premiere", "Radio Premiere"], ["review", "Written Review"], ["cover", "Home Cover (7 days)"], ["interview", "Published Interview"]];
const stage = (o) => (o.status === "delivered" ? ["Delivered", "text-[#00F5D4]"] : o.brief ? ["Brief ready", "text-[#FFB800]"] : ["Waiting for brief", "text-[#FF2E63]"]);

export const GiftDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [artists, setArtists] = useState([]);
  const [v, setV] = useState({ artist_id: "", service: "interview" });
  useEffect(() => { if (open) api.get("/admin/artists").then((r) => setArtists(r.data)); }, [open]);
  const send = async () => {
    try { await api.post("/admin/orders/gift", v); toast.success("Service gifted", { description: "The artist got an email to fill in the brief." }); setOpen(false); onDone(); }
    catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <>
      <button className="btn-ghost !px-4 !py-2 text-xs" onClick={() => setOpen(true)} data-testid="gift-open-button"><Gift className="h-4 w-4 text-[#00F5D4]" />Gift a service</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-[#0F1420] text-white" data-testid="gift-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl font-black uppercase">Gift a service</DialogTitle>
            <DialogDescription className="text-slate-400">Free for the artist — no payment. Perfect for prizes, partners or testing the full flow on your own artist account.</DialogDescription></DialogHeader>
          <select value={v.artist_id} onChange={(e) => setV({ ...v, artist_id: e.target.value })} className={sel} data-testid="gift-artist-select">
            <option value="">Choose artist…</option>
            {artists.map((a) => <option key={a.id} value={a.id}>{`${a.stage_name} · ${a.email} · ${a.status}`}</option>)}
          </select>
          <select value={v.service} onChange={(e) => setV({ ...v, service: e.target.value })} className={sel} data-testid="gift-service-select">
            {SERVICES.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
          </select>
          <button className="btn-hot" disabled={!v.artist_id} onClick={send} data-testid="gift-send-button"><Gift className="h-4 w-4" />Send gift</button>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Answer = ({ f, v, track }) => {
  if (!v || (Array.isArray(v) && !v.length)) return null;
  const body = f.type === "photos" ? (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {v.map((u, i) => (
        <a key={u} href={media(u)} target="_blank" rel="noreferrer" download className="group relative block aspect-square overflow-hidden rounded-lg border border-white/10" data-testid={`order-photo-${i}`}>
          <img src={media(u)} alt="" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
          <span className="absolute bottom-1 right-1 rounded-full bg-black/70 p-1.5"><Download className="h-3 w-3" /></span>
        </a>
      ))}
    </div>
  ) : f.type === "links" ? (
    <div className="space-y-1">{v.map((u) => <a key={u} href={u} target="_blank" rel="noreferrer" className="flex items-center gap-1 break-all text-sm text-[#00F5D4]"><ExternalLink className="h-3 w-3 shrink-0" />{u}</a>)}</div>
  ) : <p className="whitespace-pre-wrap text-sm text-slate-200">{f.type === "track" ? track || "—" : v}</p>;
  return <div className="space-y-1.5 border-b border-white/5 pb-4"><div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{f.label}</div>{body}</div>;
};

export const OrderDialog = ({ o, brief, onClose, onChange }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setText(""), [o?.id]);
  if (!o) return null;
  const ai = ["review", "interview", "feature"].includes(o.service);
  const draft = async () => {
    setBusy(true); setText("");
    try { await streamText(`/admin/orders/${o.id}/draft`, {}, (c) => setText((x) => x + c)); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const save = async () => {
    const lines = text.trim().split("\n");
    const title = lines[0].replace(/^[#*\s]+|[*\s]+$/g, "") || `${o.artist_name} — ${o.service_name}`;
    try {
      await api.post("/admin/articles", { title, kind: o.service === "review" ? "review" : "interview", content: lines.slice(1).join("\n").trim(), excerpt: "", cover: o.brief?.photos?.[0] || null, artist_id: o.artist_id, published: false });
      toast.success("Saved as a magazine draft", { description: "Open Magazine to polish and publish it." });
    } catch (er) { toast.error(errMsg(er)); }
  };
  const deliver = async () => { await api.post(`/admin/orders/${o.id}/deliver`); toast.success("Delivered — the artist got an email"); onChange(); onClose(); };
  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto border-white/10 bg-[#0F1420] text-white" data-testid="order-dialog">
        <DialogHeader>
          <span className="label-mono">{fmtDate(o.created_at)} · {o.source === "purchase" ? `€${o.price}` : o.source}</span>
          <DialogTitle className="font-display text-2xl font-black uppercase">{o.service_name}</DialogTitle>
          <DialogDescription className="text-slate-400">{o.artist_name} · {o.artist_email}</DialogDescription>
        </DialogHeader>
        {o.brief ? <div className="space-y-4" data-testid="order-brief">{brief?.fields.map((f) => <Answer key={f.id} f={f} v={o.brief[f.id]} track={o.track_title} />)}</div>
          : <p className="rounded-lg border border-[#FF2E63]/30 bg-[#FF2E63]/10 p-4 text-sm" data-testid="order-no-brief">The artist hasn't filled in the brief yet. They were asked by email and see a reminder in their dashboard.</p>}
        {ai && o.brief && (
          <div className="space-y-3 rounded-xl border border-white/10 p-4">
            <button className="btn-ghost !px-4 !py-2 text-xs" onClick={draft} disabled={busy} data-testid="order-ai-draft"><Sparkles className="h-4 w-4 text-[#00F5D4]" />{busy ? "Writing…" : text ? "Write again" : `Write the ${o.service === "review" ? "review" : "interview"} with AI`}</button>
            {text && <Textarea rows={14} value={text} onChange={(e) => setText(e.target.value)} className="border-white/10 bg-[#07090E] text-sm" data-testid="order-draft-text" />}
            {text && !busy && <button className="btn-ghost !px-4 !py-2 text-xs" onClick={save} data-testid="order-save-article"><FileText className="h-4 w-4" />Save as magazine draft</button>}
          </div>
        )}
        {o.status !== "delivered" && <button className="btn-hot" onClick={deliver} data-testid="order-deliver"><CheckCircle2 className="h-4 w-4" />Mark as delivered</button>}
      </DialogContent>
    </Dialog>
  );
};

export const OrderRow = ({ o, open, Td }) => {
  const [label, color] = stage(o);
  return (
    <tr data-testid={`admin-order-${o.id}`} className="cursor-pointer transition-colors hover:bg-white/[0.03]" onClick={() => open(o)}>
      <Td className="font-mono text-xs text-slate-400">{fmtDate(o.created_at)}</Td>
      <Td><div className="font-bold">{o.artist_name}</div><div className="text-xs text-slate-500">{o.artist_email}</div></Td>
      <Td>{o.service_name}{o.source !== "purchase" && <span className="ml-1 font-mono text-[10px] uppercase text-[#FFB800]">{o.source === "award" ? "prize" : o.source}</span>}</Td>
      <Td className="text-xs text-slate-400">{o.track_title || ""}</Td>
      <Td className="led">€{o.price}</Td>
      <Td className={`font-mono text-xs uppercase ${color}`}>{label}</Td>
      <Td><button className="btn-ghost !px-3 !py-1.5 text-xs" data-testid={`open-order-${o.id}`}>Open</button></Td>
    </tr>
  );
};
